import React, { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import DashboardBottomNav from '../../components/ui/DashboardBottomNav';


import TaskFeed from './components/TaskFeed';
import WeatherWidget from './components/WeatherWidget';
import AITreatmentSuggestions from './components/AITreatmentSuggestions';
import GuidedWalkthrough, { WALKTHROUGH_KEY } from './components/GuidedWalkthrough';
import { taskService } from '../../services/taskService';
import { realtimeSyncService } from '../../services/realtimeSyncService';

import { usePagination } from '../../hooks/usePagination';
import { cacheManager } from '../../utils/cacheManager';
import Pagination from '../../components/ui/Pagination';

const FieldStatusGrid = lazy(() => import('./components/FieldStatusGrid'));
const OperationsTimeline = lazy(() => import('./components/OperationsTimeline'));

import { CheckCircle, TrendingUp, Clock, FileText, Wifi, WifiOff, Smartphone, Bug, Leaf, ChevronDown, ChevronUp, Shield, BarChart2, HelpCircle, Plus, Calendar, Users } from 'lucide-react';
import Icon from '../../components/AppIcon';


const IPMOperationsOverview = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [fields, setFields] = useState([]);
  const [kpiData, setKpiData] = useState({});
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [showSyncPanel, setShowSyncPanel] = useState(false);
  const [showFieldStatus, setShowFieldStatus] = useState(true);
  const [showTimeline, setShowTimeline] = useState(false);
  const [showAISuggestions, setShowAISuggestions] = useState(false);

  const [syncStats, setSyncStats] = useState({});
  const [activeTeam, setActiveTeam] = useState([]);
  const [scoutingLogs, setScoutingLogs] = useState([]);
  const [inventoryTransactions, setInventoryTransactions] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('connected');

  const TASKS_CACHE_KEY = 'ipm_tasks_data';
  const FIELDS_CACHE_KEY = 'ipm_fields_data';
  const KPI_CACHE_KEY = 'ipm_kpi_data';

  const companyId = user?.companyId || '00000000-0000-0000-0000-000000000000';

  useEffect(() => {
    const loadOperationsData = async () => {
      try {
        setIsLoadingData(true);
        const cachedTasks = cacheManager?.get(TASKS_CACHE_KEY);
        const cachedFields = cacheManager?.get(FIELDS_CACHE_KEY);
        const cachedKPIs = cacheManager?.get(KPI_CACHE_KEY);

        if (cachedTasks && cachedFields && cachedKPIs) {
          setTasks(cachedTasks);
          setFields(cachedFields);
          setKpiData(cachedKPIs);
          setIsLoadingData(false);
        }

        const [tasksData, fieldsData, kpiData, stats, team] = await Promise.all([
          taskService?.getTasks(),
          Promise.resolve([]),
          Promise.resolve({}),
          realtimeSyncService?.getSyncStatistics(companyId),
          realtimeSyncService?.getActiveFieldTeam(companyId)
        ]);

        cacheManager?.set(TASKS_CACHE_KEY, tasksData || [], 10, 20);
        cacheManager?.set(FIELDS_CACHE_KEY, fieldsData || [], 15, 30);
        cacheManager?.set(KPI_CACHE_KEY, kpiData || {}, 10, 20);

        setTasks(tasksData || []);
        setFields(fieldsData || []);
        setKpiData(kpiData || {});
        setSyncStats(stats);
        setActiveTeam(team);
        setConnectionStatus('connected');
      } catch (error) {
        console.error('Error loading operations data:', error);
        setConnectionStatus('error');
      } finally {
        setIsLoadingData(false);
      }
    };

    loadOperationsData();
    setupRealtimeSubscriptions();

    const hasCompleted = localStorage.getItem(WALKTHROUGH_KEY);
    if (!hasCompleted) {
      setTimeout(() => setShowWalkthrough(true), 800);
    }

    return () => {
      realtimeSyncService?.unsubscribeAll();
    };
  }, [companyId]);

  const setupRealtimeSubscriptions = () => {
    realtimeSyncService?.subscribeToScoutingLogs(companyId, (payload) => {
      if (payload?.eventType === 'INSERT' && payload?.new) {
        setScoutingLogs(prev => [payload?.new, ...prev]?.slice(0, 10));
      }
    });
    realtimeSyncService?.subscribeToInventoryTransactions(companyId, (payload) => {
      if (payload?.eventType === 'INSERT' && payload?.new) {
        setInventoryTransactions(prev => [payload?.new, ...prev]?.slice(0, 10));
      }
    });
    realtimeSyncService?.subscribeToDeviceActivity(companyId, (payload) => {
      if (payload?.eventType === 'UPDATE' && payload?.new) {
        refreshActiveTeam();
      }
    });
  };

  const refreshActiveTeam = async () => {
    try {
      const team = await realtimeSyncService?.getActiveFieldTeam(companyId);
      setActiveTeam(team);
    } catch (err) {
      console.error('Error refreshing active team:', err);
    }
  };

  const {
    currentData: paginatedTasks,
    currentPage: tasksPage,
    totalPages: tasksTotalPages,
    goToPage: goToTasksPage,
    getPageRange: getTasksPageRange,
    startIndex: tasksStartIndex,
    endIndex: tasksEndIndex,
    totalItems: tasksTotalItems
  } = usePagination(tasks, 10);

  const calculatedKpis = useMemo(() => {
    const totalTasks = tasks?.length;
    const completedTasks = tasks?.filter(t => t?.status === 'completed')?.length;
    const pendingTasks = tasks?.filter(t => t?.status === 'pending')?.length;
    const inProgressTasks = tasks?.filter(t => t?.status === 'in_progress')?.length;
    const completionRate = totalTasks > 0 ? ((completedTasks / totalTasks) * 100)?.toFixed(1) : 0;
    return { totalTasks, completedTasks, pendingTasks, inProgressTasks, completionRate };
  }, [tasks]);

  const LoadingSpinner = () => (
    <div className="flex items-center justify-center h-48">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
    </div>
  );

  /* Reusable collapsible section header for mobile */
  const CollapsibleHeader = ({ title, subtitle, icon: SectionIcon, iconBg, iconColor, isOpen, onToggle, badge }) => (
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
    >
      <div className="flex items-center gap-3">
        {SectionIcon && (
          <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center flex-shrink-0`}>
            <SectionIcon className={`w-4 h-4 ${iconColor}`} />
          </div>
        )}
        <div>
          <p className="text-sm font-semibold text-gray-800">{title}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {badge && <span className="text-xs text-gray-500 font-medium hidden sm:block">{badge}</span>}
        {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </div>
    </button>
  );

  if (isLoadingData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingSpinner />
        </div>
        <DashboardBottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {showWalkthrough && (
        <GuidedWalkthrough onClose={() => setShowWalkthrough(false)} />
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5 pb-24 md:pb-10">

        {/* ── Page Header ── */}
        <div className="flex items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-green-600 flex items-center justify-center flex-shrink-0">
              <Leaf className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 leading-tight truncate">IPM Dashboard</h1>
              <p className="text-xs text-gray-500 truncate">Integrated Pest Management · All operation sizes</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setShowWalkthrough(true)}
              className="flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1.5 bg-white border border-gray-200 text-gray-600 hover:text-green-700 hover:border-green-300 rounded-lg text-xs sm:text-sm font-medium transition-colors shadow-sm"
              title="Replay guided tour"
            >
              <HelpCircle className="w-4 h-4" />
              <span className="hidden sm:inline">Tour</span>
            </button>
            <div className={`flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium ${connectionStatus === 'connected' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {connectionStatus === 'connected' ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
              <span>{connectionStatus === 'connected' ? 'Live' : 'Offline'}</span>
            </div>
          </div>
        </div>

        {/* ── 3-Step IPM Strip — horizontal scroll on mobile, grid on sm+ ── */}
        <div className="mb-5">
          {/* Mobile: horizontal scroll row */}
          <div className="flex gap-3 overflow-x-auto pb-1 sm:hidden scrollbar-hide">
            <div className="flex items-center gap-3 bg-white border border-green-100 rounded-xl px-4 py-3 shadow-sm flex-shrink-0 min-w-[200px]">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <Bug className="w-4 h-4 text-green-700" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">1. Monitor Pests</p>
                <p className="text-xs text-gray-500">Track pest activity</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white border border-blue-100 rounded-xl px-4 py-3 shadow-sm flex-shrink-0 min-w-[200px]">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Shield className="w-4 h-4 text-blue-700" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">2. Plan Treatments</p>
                <p className="text-xs text-gray-500">Schedule pest control</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white border border-purple-100 rounded-xl px-4 py-3 shadow-sm flex-shrink-0 min-w-[200px]">
              <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                <BarChart2 className="w-4 h-4 text-purple-700" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">3. Analyze Results</p>
                <p className="text-xs text-gray-500">AI-powered insights</p>
              </div>
            </div>
          </div>
          {/* sm+: 3-column grid */}
          <div className="hidden sm:grid sm:grid-cols-3 gap-3">
            <div className="flex items-center gap-3 bg-white border border-green-100 rounded-xl px-4 py-3 shadow-sm">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <Bug className="w-4 h-4 text-green-700" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">1. Monitor Pests</p>
                <p className="text-xs text-gray-500">Track pest activity across your fields</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white border border-blue-100 rounded-xl px-4 py-3 shadow-sm">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Shield className="w-4 h-4 text-blue-700" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">2. Plan Treatments</p>
                <p className="text-xs text-gray-500">Schedule and log pest control actions</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white border border-purple-100 rounded-xl px-4 py-3 shadow-sm">
              <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                <BarChart2 className="w-4 h-4 text-purple-700" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">3. Analyze Results</p>
                <p className="text-xs text-gray-500">See what's working with AI insights</p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Today's Numbers — 2-col on mobile, 4-col on sm+ ── */}
        <div className="mb-5">
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Today at a Glance</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{calculatedKpis?.completedTasks}</p>
                <p className="text-xs text-gray-500 leading-tight">Tasks Done</p>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-yellow-50 flex items-center justify-center flex-shrink-0">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{calculatedKpis?.inProgressTasks}</p>
                <p className="text-xs text-gray-500 leading-tight">In Progress</p>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{activeTeam?.length || 0}</p>
                <p className="text-xs text-gray-500 leading-tight">Field Devices</p>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{calculatedKpis?.completionRate}%</p>
                <p className="text-xs text-gray-500 leading-tight">Completion</p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Mobile Quick Actions (visible only on mobile, above main content) ── */}
        <div className="sm:hidden mb-5">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Quick Actions</h3>
            <div className="grid grid-cols-3 gap-2">
              <button className="flex flex-col items-center gap-1.5 px-2 py-3 bg-green-600 text-white rounded-xl text-xs font-medium hover:bg-green-700 transition-colors min-h-[60px] justify-center">
                <Plus className="w-5 h-5" />
                <span>New Task</span>
              </button>
              <button className="flex flex-col items-center gap-1.5 px-2 py-3 bg-white border border-gray-200 text-gray-700 rounded-xl text-xs font-medium hover:bg-gray-50 transition-colors min-h-[60px] justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
                <span>Report</span>
              </button>
              <button className="flex flex-col items-center gap-1.5 px-2 py-3 bg-white border border-gray-200 text-gray-700 rounded-xl text-xs font-medium hover:bg-gray-50 transition-colors min-h-[60px] justify-center">
                <Calendar className="w-5 h-5 text-purple-600" />
                <span>Schedule</span>
              </button>
            </div>
          </div>
        </div>

        {/* ── Main Content ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* Left column — main panels */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-5">

            {/* Field Status — collapsible on mobile, always open on desktop */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              {/* Mobile: collapsible header */}
              <div className="sm:hidden">
                <CollapsibleHeader
                  title="Field Status"
                  subtitle="Current pest pressure per field"
                  icon={Bug}
                  iconBg="bg-green-50"
                  iconColor="text-green-600"
                  isOpen={showFieldStatus}
                  onToggle={() => setShowFieldStatus(v => !v)}
                />
              </div>
              {/* Desktop: static header */}
              <div className="hidden sm:block px-5 py-4 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-800">Field Status</h2>
                <p className="text-xs text-gray-500 mt-0.5">Current pest pressure level for each field</p>
              </div>
              {/* Content: always visible on desktop, toggled on mobile */}
              <div className={`${showFieldStatus ? 'block' : 'hidden'} sm:block`}>
                <div className="border-t border-gray-100 sm:border-t-0 p-4">
                  <Suspense fallback={<LoadingSpinner />}>
                    <FieldStatusGrid fields={fields} />
                  </Suspense>
                </div>
              </div>
            </div>

            {/* Active Tasks — always visible, most important for field teams */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-800">Active Tasks</h2>
                <p className="text-xs text-gray-500 mt-0.5">What your team is working on right now</p>
              </div>
              <div className="p-4">
                <TaskFeed tasks={paginatedTasks} />
              </div>
              <Pagination
                currentPage={tasksPage}
                totalPages={tasksTotalPages}
                onPageChange={goToTasksPage}
                pageRange={getTasksPageRange()}
                startIndex={tasksStartIndex}
                endIndex={tasksEndIndex}
                totalItems={tasksTotalItems}
              />
            </div>

            {/* Treatment Timeline — collapsible on mobile */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              {/* Mobile: collapsible header */}
              <div className="sm:hidden">
                <CollapsibleHeader
                  title="Treatment Timeline"
                  subtitle="Scheduled and completed pest treatments"
                  icon={Calendar}
                  iconBg="bg-blue-50"
                  iconColor="text-blue-600"
                  isOpen={showTimeline}
                  onToggle={() => setShowTimeline(v => !v)}
                />
              </div>
              {/* Desktop: static header */}
              <div className="hidden sm:block px-5 py-4 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-800">Treatment Timeline</h2>
                <p className="text-xs text-gray-500 mt-0.5">Scheduled and completed pest treatments</p>
              </div>
              <div className={`${showTimeline ? 'block' : 'hidden'} sm:block`}>
                <div className="border-t border-gray-100 sm:border-t-0 p-4">
                  <Suspense fallback={<LoadingSpinner />}>
                    <OperationsTimeline tasks={tasks} />
                  </Suspense>
                </div>
              </div>
            </div>

            {/* Field Team & Sync — collapsed by default on all sizes */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <button
                onClick={() => setShowSyncPanel(v => !v)}
                className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                    <Smartphone className="w-4 h-4 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Field Team & Live Sync</p>
                    <p className="text-xs text-gray-500">Real-time data from mobile devices in the field</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-indigo-600 font-medium">{activeTeam?.length || 0} online</span>
                  {showSyncPanel ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </div>
              </button>

              {showSyncPanel && (
                <div className="border-t border-gray-100 p-4 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {/* Scouting Logs */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-semibold text-gray-800">Pest Scouting Logs</p>
                        <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded-full">{syncStats?.scoutingLogsToday || 0} today</span>
                      </div>
                      <div className="space-y-2">
                        {scoutingLogs?.slice(0, 3)?.map((log, idx) => (
                          <div key={idx} className="text-xs">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-gray-700">{log?.fieldName || 'Field'}</span>
                              <span className="text-gray-400">{new Date(log?.createdAt)?.toLocaleTimeString()}</span>
                            </div>
                            <p className="text-gray-500 mt-0.5">{log?.pestType || 'Pest detected'} — {log?.severity || 'Low'} severity</p>
                          </div>
                        ))}
                        {scoutingLogs?.length === 0 && <p className="text-xs text-gray-400 italic">No recent scouting logs</p>}
                      </div>
                    </div>

                    {/* Product Usage */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-semibold text-gray-800">Product Usage</p>
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">{syncStats?.inventoryTransactionsToday || 0} today</span>
                      </div>
                      <div className="space-y-2">
                        {inventoryTransactions?.slice(0, 3)?.map((txn, idx) => (
                          <div key={idx} className="text-xs">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-gray-700">{txn?.productName || 'Product'}</span>
                              <span className="text-gray-400">{new Date(txn?.createdAt)?.toLocaleTimeString()}</span>
                            </div>
                            <p className="text-gray-500 mt-0.5">{txn?.transactionType || 'Used'}: {txn?.quantity || 0} {txn?.unit || 'units'}</p>
                          </div>
                        ))}
                        {inventoryTransactions?.length === 0 && <p className="text-xs text-gray-400 italic">No recent product usage</p>}
                      </div>
                    </div>

                    {/* Active Team */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-semibold text-gray-800">Active Team</p>
                        <span className="px-2 py-0.5 bg-indigo-100 text-indigo-800 text-xs font-medium rounded-full">{activeTeam?.length || 0} online</span>
                      </div>
                      <div className="space-y-2">
                        {activeTeam?.slice(0, 3)?.map((member, idx) => (
                          <div key={idx} className="flex items-center justify-between text-xs">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                              <span className="text-gray-700">{member?.userName || 'Team Member'}</span>
                            </div>
                            <span className="text-gray-400">{member?.deviceType || 'Mobile'}</span>
                          </div>
                        ))}
                        {activeTeam?.length === 0 && <p className="text-xs text-gray-400 italic">No active team members</p>}
                      </div>
                    </div>
                  </div>

                  {/* Sync Stats */}
                  <div className="grid grid-cols-4 gap-3 pt-3 border-t border-gray-200 text-center">
                    <div>
                      <p className="text-xl font-bold text-indigo-600">{syncStats?.totalSyncsToday || 0}</p>
                      <p className="text-xs text-gray-500">Syncs Today</p>
                    </div>
                    <div>
                      <p className="text-xl font-bold text-green-600">{syncStats?.successfulSyncs || 0}</p>
                      <p className="text-xs text-gray-500">Successful</p>
                    </div>
                    <div>
                      <p className="text-xl font-bold text-red-500">{syncStats?.failedSyncs || 0}</p>
                      <p className="text-xs text-gray-500">Failed</p>
                    </div>
                    <div>
                      <p className="text-xl font-bold text-purple-600">{syncStats?.avgSyncTime || 0}s</p>
                      <p className="text-xs text-gray-500">Avg Time</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right sidebar — hidden on mobile (Quick Actions shown above), visible on lg+ */}
          <div className="lg:col-span-1 space-y-5">

            {/* Quick Actions — desktop only (mobile version shown above) */}
            <div className="hidden sm:block bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <h3 className="text-sm font-semibold text-gray-800 mb-1">Quick Actions</h3>
              <p className="text-xs text-gray-500 mb-4">Common tasks you can do right now</p>
              <div className="space-y-2.5">
                <button className="w-full px-4 py-3 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 min-h-[44px]">
                  <Plus className="w-4 h-4" />
                  Create New Task
                </button>
                <button className="w-full px-4 py-3 text-sm font-medium bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 min-h-[44px]">
                  <FileText className="w-4 h-4 text-blue-600" />
                  Generate Report
                </button>
                <button className="w-full px-4 py-3 text-sm font-medium bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 min-h-[44px]">
                  <Calendar className="w-4 h-4 text-purple-600" />
                  Schedule Treatment
                </button>
              </div>
            </div>

            {/* Weather */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-800">Weather Conditions</h2>
                <p className="text-xs text-gray-500 mt-0.5">Current conditions affecting pest activity</p>
              </div>
              <div className="p-4">
                <WeatherWidget />
              </div>
            </div>

            {/* AI Suggestions — collapsible on mobile */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              {/* Mobile: collapsible */}
              <div className="sm:hidden">
                <CollapsibleHeader
                  title="AI Treatment Suggestions"
                  subtitle="Smart recommendations based on current data"
                  icon={BarChart2}
                  iconBg="bg-purple-50"
                  iconColor="text-purple-600"
                  isOpen={showAISuggestions}
                  onToggle={() => setShowAISuggestions(v => !v)}
                />
              </div>
              {/* Desktop: static header */}
              <div className="hidden sm:block px-5 py-4 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-800">AI Treatment Suggestions</h2>
                <p className="text-xs text-gray-500 mt-0.5">Smart recommendations based on current data</p>
              </div>
              <div className={`${showAISuggestions ? 'block' : 'hidden'} sm:block`}>
                <div className="border-t border-gray-100 sm:border-t-0 p-4">
                  <AITreatmentSuggestions
                    weatherData={{ temperature: 24, humidity: 65, windSpeed: 8, condition: 'Partly Cloudy', uvIndex: 6 }}
                    scoutingLogs={scoutingLogs}
                    tasks={tasks}
                  />
                </div>
              </div>
            </div>

            {/* Support Links */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Legal</h3>
              <div className="space-y-1">
                <a href="/privacy-policy" className="flex items-center text-sm text-gray-600 hover:text-green-600 transition-colors py-2 px-2 rounded-lg hover:bg-gray-50">
                  <FileText className="w-4 h-4 mr-2 flex-shrink-0 text-gray-400" />
                  Privacy Policy
                </a>
                <a href="/terms-conditions" className="flex items-center text-sm text-gray-600 hover:text-green-600 transition-colors py-2 px-2 rounded-lg hover:bg-gray-50">
                  <FileText className="w-4 h-4 mr-2 flex-shrink-0 text-gray-400" />
                  Terms & Conditions
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <DashboardBottomNav />
    </div>
  );
};

export default IPMOperationsOverview;